import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../api/axios";

const BULK_API = "/admin/products/products";
const PAGE_NAME = "Products";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The global Axios interceptor handles the 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const ProductBulkUpload = () => {
  const navigate = useNavigate();

  const pollingRef = useRef(null);
  const isMountedRef = useRef(true);
  const pollFailCountRef = useRef(0);

  const [excelFile, setExcelFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);

  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  const [expandedSku, setExpandedSku] = useState(null);
  const [previewFilter, setPreviewFilter] = useState("all");
  const [templateLoading, setTemplateLoading] = useState(false);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const fetchBulkProgress = async (jobId) => {
    try {
      const response = await api.get(
        `${BULK_API}/bulk-progress/${jobId}`,
        {
          headers: getPermissionHeaders("view"),
          params: {
            _t: Date.now(),
          },
        }
      );

      pollFailCountRef.current = 0;

      if (!isMountedRef.current) return;

      const progress = response.data?.progress || null;

      setBulkProgress(progress);

      const finishedStatuses = [
        "completed",
        "failed",
        "not_found",
      ];

      if (finishedStatuses.includes(progress?.status)) {
        stopPolling();
        setBulkLoading(false);

        if (progress?.status === "completed") {
          const errors = Number(
            progress?.result?.summary?.errors || 0
          );

          if (errors > 0) {
            toast.warning(
              `Bulk upload completed with ${errors} error(s).`
            );
          } else {
            toast.success(
              "Bulk upload completed successfully."
            );
          }
        }

        if (progress?.status === "failed") {
          toast.error(
            progress?.message || "Bulk upload failed."
          );
        }

        if (progress?.status === "not_found") {
          toast.error(
            "Bulk progress not found or expired."
          );
        }

        return;
      }

      pollingRef.current = setTimeout(
        () => fetchBulkProgress(jobId),
        1000
      );
    } catch (error) {
      if (!isMountedRef.current) return;

      /*
       * Do not keep polling when the request is forbidden.
       * The Axios interceptor opens the global access-denied dialog.
       */
      if (error.response?.status === 403) {
        stopPolling();
        setBulkLoading(false);
        return;
      }

      /*
       * Stop if the session has expired and refresh also failed.
       */
      if (error.response?.status === 401) {
        stopPolling();
        setBulkLoading(false);
        return;
      }

      pollFailCountRef.current += 1;

      if (pollFailCountRef.current >= 10) {
        stopPolling();
        setBulkLoading(false);

        showRequestError(
          error,
          "Unable to fetch upload progress."
        );

        return;
      }

      pollingRef.current = setTimeout(
        () => fetchBulkProgress(jobId),
        2000
      );
    }
  };

  const startPolling = (jobId) => {
    if (!jobId) return;

    pollFailCountRef.current = 0;
    stopPolling();
    fetchBulkProgress(jobId);
  };

  const handleExcelChange = (event) => {
    setExcelFile(
      event.target.files?.[0] || null
    );

    setBulkPreview(null);
    setBulkProgress(null);
  };

  const handleZipChange = (event) => {
    setZipFile(
      event.target.files?.[0] || null
    );

    setBulkPreview(null);
    setBulkProgress(null);
  };

  const downloadBulkTemplate = async () => {
    try {
      setTemplateLoading(true);

      const response = await api.get(
        `${BULK_API}/bulk-template`,
        {
          headers: getPermissionHeaders("view"),
          responseType: "blob",
        }
      );
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || "product-bulk-upload.xlsx";
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      showRequestError(error, "Bulk upload template could not be downloaded.");
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleBulkPreview = async (event) => {
    event.preventDefault();

    if (!excelFile) {
      toast.error("Excel file is required.");
      return;
    }

    try {
      stopPolling();

      setBulkLoading(true);
      setBulkPreview(null);
      setBulkProgress(null);
      setExpandedSku(null);
      setPreviewFilter("all");

      const formData = new FormData();

      formData.append("excel", excelFile);

      if (zipFile) {
        formData.append("assets", zipFile);
      }

      const response = await api.post(
        `${BULK_API}/bulk-preview`,
        formData,
        {
          /*
           * Do not manually set Content-Type.
           * Axios/browser adds the multipart boundary.
           */
          headers: getPermissionHeaders("add"),
        }
      );

      const previewData =
        response.data?.data || response.data || null;

      setBulkPreview(previewData);

      setExpandedSku(
        previewData?.products?.[0]?.sourceRow || null
      );

      toast.success("Bulk preview generated.");
    } catch (error) {
      showRequestError(
        error,
        "Bulk preview failed."
      );
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleBulkProduct = (sourceRow) => {
    setBulkPreview((previous) => {
      if (!previous) return previous;

      return {
        ...previous,

        products: (previous.products || []).map(
          (product) =>
            Number(product.sourceRow) === Number(sourceRow)
              ? {
                  ...product,
                  include: !product.include,
                }
              : product
        ),
      };
    });
  };

  const confirmBulkUpload = async () => {
    if (!bulkPreview?.previewId) {
      toast.error(
        "Bulk preview ID is missing. Generate the preview again."
      );
      return;
    }

    if (bulkPreview.fileErrors?.length > 0) {
      toast.error("Resolve workbook-level errors before confirming.");
      return;
    }

    const selectedProducts = (
      bulkPreview.products || []
    ).filter((product) => product.include);

    if (selectedProducts.length === 0) {
      toast.error(
        "Please select at least one product."
      );
      return;
    }

    const invalidSelected =
      selectedProducts.filter(
        (product) =>
          product.validationErrors?.length > 0
      );

    if (invalidSelected.length > 0) {
      toast.error(
        "Please fix or unselect invalid products before confirming."
      );
      return;
    }

    try {
      stopPolling();
      setBulkLoading(true);

      setBulkProgress({
        status: "queued",
        message: "Bulk upload queued...",
        percentage: 0,
        total: selectedProducts.length,
        completed: 0,
        created: 0,
        updated: 0,
        skipped: 0,
        errors: 0,
      });

      const response = await api.post(
        `${BULK_API}/bulk-confirm`,
        {
          previewId: bulkPreview.previewId,
          selectedRows: selectedProducts.map((product) => product.sourceRow),
        },
        {
          headers: getPermissionHeaders("add"),
        }
      );

      const returnedJobId =
        response.data?.jobId ||
        response.data?.data?.jobId ||
        null;

      if (!returnedJobId) {
        toast.error(
          "Bulk upload started, but no job ID was returned."
        );

        setBulkLoading(false);
        return;
      }

      setBulkPreview(null);
      setExpandedSku(null);

      toast.info("Bulk upload started.");

      startPolling(returnedJobId);
    } catch (error) {
      setBulkLoading(false);

      showRequestError(
        error,
        "Bulk confirmation failed."
      );
    }
  };

  const clearPreview = () => {
    stopPolling();

    setBulkPreview(null);
    setBulkProgress(null);
    setBulkLoading(false);
    setExpandedSku(null);
    setPreviewFilter("all");
  };

  const setAllValidProducts = (include) => {
    setBulkPreview((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        products: (previous.products || []).map((product) => ({
          ...product,
          include:
            product.validationErrors?.length > 0 ? false : Boolean(include),
        })),
      };
    });
  };

  const downloadValidationErrors = () => {
    if (!bulkPreview) return;

    const rows = [
      ["sheet", "row", "column", "sku", "message"],
      ...(bulkPreview.fileErrors || []).map((issue) => [
        issue.sheet,
        issue.row,
        issue.column,
        "",
        issue.message,
      ]),
      ...(bulkPreview.products || []).flatMap((product) =>
        (product.validationIssues || []).map((issue) => [
          issue.sheet,
          issue.row,
          issue.column,
          product.sku,
          issue.message,
        ])
      ),
    ];

    const csv = rows
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" })
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "bulk-upload-validation-errors.csv";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, []);

  const getProductStatus = (product) => {
    if (product.validationErrors?.length > 0) {
      return "Invalid";
    }

    if (product.warnings?.length > 0) {
      return "Ready with warnings";
    }

    return "Ready";
  };

  const getSelectedCount = () => {
    return (
      bulkPreview?.products?.filter(
        (product) => product.include
      ).length || 0
    );
  };

  const getVisibleProducts = () => {
    const products = bulkPreview?.products || [];
    if (previewFilter === "ready") {
      return products.filter((product) => !product.validationErrors?.length);
    }
    if (previewFilter === "invalid") {
      return products.filter((product) => product.validationErrors?.length > 0);
    }
    if (previewFilter === "create" || previewFilter === "update") {
      return products.filter((product) => product.operation === previewFilter);
    }
    return products;
  };

  const getPriceText = (product) => {
    const finalPrice = Number(
      product.final_price ||
        product.base_price ||
        0
    );

    const basePrice = Number(
      product.base_price || 0
    );

    if (finalPrice !== basePrice) {
      return `₹${finalPrice.toLocaleString(
        "en-IN"
      )} / Base ₹${basePrice.toLocaleString(
        "en-IN"
      )}`;
    }

    return `₹${basePrice.toLocaleString(
      "en-IN"
    )}`;
  };

  return (
    <section className="admin-page">
      <div className="page-header">
        <div>
          <p>Catalogue</p>

          <h1>Bulk Product Upload</h1>

          <span>
            Download the live workbook, add products and
            customization overrides, then preview every change
            before it is applied.
          </span>
        </div>

        <div className="bulk-header-actions">
          <button
            type="button"
            className="primary-action"
            onClick={downloadBulkTemplate}
            disabled={templateLoading}
          >
            {templateLoading ? "Preparing Template..." : "Download Excel Template"}
          </button>

          <button
            type="button"
            className="secondary-action"
            onClick={() => navigate("/products")}
          >
            Back to Products
          </button>
        </div>
      </div>

      <div className="page-card bulk-card">
        <div className="bulk-format-note">
          <h3>Customization-aware Excel format</h3>

          <p>
            Use the generated workbook. Add core catalogue data on
            <strong> Products</strong>, attach reusable choices on
            <strong> Product Templates</strong>, and enter product-specific
            prices or availability on <strong>Option Overrides</strong>.
          </p>

          <p>
            Create and update are explicit. Empty cells in an update keep
            their current value, while template and media modes control
            whether existing data is kept, merged, appended or replaced.
          </p>

          <p>
            Use the stable keys in the workbook's Reference Data sheet.
            ZIP folders can contain .jpg, .jpeg, .png, .webp, .mp4, .mov
            and .webm files.
          </p>
        </div>

        {!bulkLoading && (
          <form
            className="bulk-upload-form"
            onSubmit={handleBulkPreview}
          >
            <label>
              Excel File

              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleExcelChange}
              />
            </label>

            <label>
              Assets ZIP

              <input
                type="file"
                accept=".zip"
                onChange={handleZipChange}
              />
            </label>

            <button
              type="submit"
              className="primary-action"
              disabled={
                bulkLoading || !excelFile
              }
            >
              Preview Upload
            </button>
          </form>
        )}

        {bulkProgress && (
          <div className="bulk-progress">
            <div className="progress-top">
              <strong>
                {bulkProgress.message ||
                  bulkProgress.status}
              </strong>

              <span>
                {Number(
                  bulkProgress.percentage || 0
                )}
                %
              </span>
            </div>

            <div className="progress-track">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min(
                    100,
                    Math.max(
                      0,
                      Number(
                        bulkProgress.percentage ||
                          0
                      )
                    )
                  )}%`,
                }}
              />
            </div>

            <div className="bulk-product-meta">
              <span>
                <strong>Total</strong>
                {bulkProgress.total || 0}
              </span>

              <span>
                <strong>Completed</strong>
                {bulkProgress.completed || 0}
              </span>

              <span>
                <strong>Created</strong>
                {bulkProgress.created || 0}
              </span>

              <span>
                <strong>Updated</strong>
                {bulkProgress.updated || 0}
              </span>

              <span>
                <strong>Errors</strong>
                {bulkProgress.errors || 0}
              </span>
            </div>

            {bulkProgress.currentSku && (
              <p className="bulk-current-sku">
                Current SKU:{" "}
                <strong>
                  {bulkProgress.currentSku}
                </strong>
              </p>
            )}

            {bulkProgress.result && (
              <div className="bulk-error-box">
                <p>
                  <strong>Created:</strong>{" "}
                  {bulkProgress.result.summary
                    ?.created || 0}
                </p>

                <p>
                  <strong>Updated:</strong>{" "}
                  {bulkProgress.result.summary
                    ?.updated || 0}
                </p>

                <p>
                  <strong>Errors:</strong>{" "}
                  {bulkProgress.result.summary
                    ?.errors || 0}
                </p>

                {(
                  bulkProgress.result.created || []
                ).map((item, index) => (
                  <p
                    key={`created-${
                      item.sku || index
                    }`}
                  >
                    <strong>{item.sku}:</strong>{" "}
                    Created — Media uploaded:{" "}
                    {item.media_uploaded || 0},
                    Templates attached:{" "}
                    {item.attached_templates
                      ?.length || 0}
                  </p>
                ))}

                {(
                  bulkProgress.result.updated || []
                ).map((item, index) => (
                  <p
                    key={`updated-${
                      item.sku || index
                    }`}
                  >
                    <strong>{item.sku}:</strong>{" "}
                    Updated — Media uploaded:{" "}
                    {item.media_uploaded || 0},
                    Templates attached:{" "}
                    {item.attached_templates
                      ?.length || 0}
                  </p>
                ))}

                {(
                  bulkProgress.result.errors || []
                ).map((item, index) => (
                  <div
                    key={`error-${
                      item.sku || index
                    }`}
                  >
                    <p>
                      <strong>
                        {item.sku || "Unknown SKU"}:
                      </strong>{" "}
                      {item.error ||
                        "Unknown upload error"}
                    </p>

                    {(item.details || []).map(
                      (detail, detailIndex) => (
                        <p
                          key={`${detail.path}-${detailIndex}`}
                          style={{
                            marginLeft: 16,
                          }}
                        >
                          {detail.path || "Field"}:{" "}
                          {detail.message ||
                            "Invalid value"}{" "}
                          {detail.value !==
                          undefined
                            ? `(${String(
                                detail.value
                              )})`
                            : ""}
                        </p>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {bulkPreview && !bulkLoading && (
          <div className="bulk-preview">
            <div className="bulk-summary">
              <div>
                <span>Total Products</span>

                <strong>
                  {bulkPreview.summary
                    ?.totalProducts ||
                    bulkPreview.products?.length ||
                    0}
                </strong>
              </div>

              <div>
                <span>Selected</span>
                <strong>
                  {getSelectedCount()}
                </strong>
              </div>

              <div>
                <span>Ready</span>

                <strong>
                  {bulkPreview.summary
                    ?.readyProducts || 0}
                </strong>
              </div>

              <div>
                <span>Invalid</span>

                <strong>
                  {bulkPreview.summary
                    ?.invalidProducts || 0}
                </strong>
              </div>

              <div>
                <span>Create</span>
                <strong>{bulkPreview.summary?.createProducts || 0}</strong>
              </div>

              <div>
                <span>Update</span>
                <strong>{bulkPreview.summary?.updateProducts || 0}</strong>
              </div>

              <div>
                <span>Customized</span>
                <strong>{bulkPreview.summary?.customizedProducts || 0}</strong>
              </div>

              <div>
                <span>Workbook Errors</span>
                <strong>{bulkPreview.summary?.fileErrors || 0}</strong>
              </div>
            </div>

            <div className="bulk-preview-toolbar">
              <label>
                Show
                <select
                  value={previewFilter}
                  onChange={(event) => setPreviewFilter(event.target.value)}
                >
                  <option value="all">All products</option>
                  <option value="ready">Ready</option>
                  <option value="invalid">Invalid</option>
                  <option value="create">Create</option>
                  <option value="update">Update</option>
                </select>
              </label>

              <button
                type="button"
                className="ghost-action"
                onClick={() => setAllValidProducts(true)}
              >
                Select all valid
              </button>
              <button
                type="button"
                className="ghost-action"
                onClick={() => setAllValidProducts(false)}
              >
                Unselect all
              </button>

              {(
                (bulkPreview.fileErrors?.length || 0) +
                (bulkPreview.summary?.invalidProducts || 0)
              ) > 0 && (
                <button
                  type="button"
                  className="secondary-action"
                  onClick={downloadValidationErrors}
                >
                  Download validation errors
                </button>
              )}
            </div>

            {bulkPreview.fileErrors?.length > 0 && (
              <div className="bulk-error-box">
                <strong>Workbook-level errors</strong>
                {bulkPreview.fileErrors.map((issue, index) => (
                  <p key={`${issue.sheet}-${issue.row}-${index}`}>
                    {issue.sheet} row {issue.row}, {issue.column}: {issue.message}
                  </p>
                ))}
              </div>
            )}

            <div className="bulk-product-list">
              {getVisibleProducts().map(
                (product, productIndex) => {
                  const isExpanded =
                    Number(expandedSku) === Number(product.sourceRow);

                  const hasErrors =
                    product.validationErrors
                      ?.length > 0;

                  const hasWarnings =
                    product.warnings?.length > 0;

                  return (
                    <div
                      key={
                        product.sourceRow || product.sku ||
                        `product-${productIndex}`
                      }
                      className={`bulk-product-card ${
                        hasErrors
                          ? "has-error"
                          : hasWarnings
                            ? "has-warning"
                            : ""
                      } ${
                        !product.include
                          ? "is-disabled"
                          : ""
                      }`}
                    >
                      <div className="bulk-product-top">
                        <label className="bulk-check">
                          <input
                            type="checkbox"
                            checked={Boolean(
                              product.include
                            )}
                            disabled={hasErrors}
                            onChange={() =>
                              toggleBulkProduct(
                                product.sourceRow
                              )
                            }
                          />
                        </label>

                        <div className="bulk-product-title">
                          <h3>
                            {product.product_name ||
                              "Unnamed Product"}
                          </h3>

                          <p>
                            {product.sku ||
                              "No SKU"}
                          </p>
                        </div>

                        <div className="bulk-badges">
                          <span className={`status-badge bulk-operation-${product.operation}`}>
                            {product.operation === "update" ? "Update" : "Create"}
                          </span>

                          <span
                            className={`status-badge ${
                              hasErrors
                                ? "danger"
                                : hasWarnings
                                  ? "draft"
                                  : "success"
                            }`}
                          >
                            {getProductStatus(
                              product
                            )}
                          </span>

                          <span className="status-badge">
                            {product.template_names
                              ?.length || 0}{" "}
                            Templates
                          </span>

                          <span className="status-badge">
                            {product.generalMediaCount ||
                              0}{" "}
                            Media
                          </span>

                          <span className="status-badge">
                            {product.option_value_overrides?.length || 0}{" "}
                            Option Values
                          </span>
                        </div>

                        <button
                          type="button"
                          className="ghost-action"
                          onClick={() =>
                            setExpandedSku(
                              isExpanded
                                ? null
                                : product.sourceRow
                            )
                          }
                        >
                          {isExpanded
                            ? "Hide Details"
                            : "Review Details"}
                        </button>
                      </div>

                      <div className="bulk-product-meta">
                        <span>
                          <strong>
                            Category
                          </strong>

                          {product.category || "-"}{" "}
                          /{" "}
                          {product.subcategory ||
                            product.sub_category ||
                            "-"}
                        </span>

                        <span>
                          <strong>Price</strong>
                          {getPriceText(product)}
                        </span>

                        <span>
                          <strong>
                            Media Folder
                          </strong>

                          {product.media_folder ||
                            "-"}
                        </span>

                        <span>
                          <strong>Stock</strong>

                          {product.stock_status ||
                            "-"}
                        </span>

                        <span>
                          <strong>Status</strong>

                          {product.status || "-"}
                        </span>

                        <span>
                          <strong>HSN/SAC</strong>
                          {product.hsn_sac_code || "-"}
                        </span>

                        <span>
                          <strong>
                            Printable
                          </strong>

                          {product.is_printable
                            ? "Yes"
                            : "No"}
                        </span>

                        <span>
                          <strong>Questionnaire</strong>
                          {product.requires_order_questionnaire ? "Required" : "Not required"}
                        </span>

                        <span>
                          <strong>Update Modes</strong>
                          Templates: {product.templates_mode || "-"}; Media:{" "}
                          {product.media_mode || "-"}
                        </span>
                      </div>

                      {product.template_names
                        ?.length > 0 && (
                        <div className="bulk-general-preview">
                          <strong>
                            Attached Option Templates
                          </strong>

                          <div className="bulk-option-values">
                            {product.template_names.map(
                              (
                                template,
                                templateIndex
                              ) => (
                                <span
                                  key={`${template}-${templateIndex}`}
                                >
                                  {template}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {product.previewImages
                        ?.length > 0 && (
                        <div className="bulk-general-preview">
                          <strong>
                            Image Preview
                          </strong>

                          <div className="bulk-image-row">
                            {product.previewImages.map(
                              (src, index) => (
                                <img
                                  key={`${src}-${index}`}
                                  src={src}
                                  alt={
                                    product.product_name ||
                                    "Product preview"
                                  }
                                />
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {product.previewVideos
                        ?.length > 0 && (
                        <div className="bulk-general-preview">
                          <strong>
                            Video Preview
                          </strong>

                          <div className="bulk-image-row">
                            {product.previewVideos.map(
                              (src, index) => (
                                <video
                                  key={`${src}-${index}`}
                                  src={src}
                                  controls
                                  muted
                                  playsInline
                                  style={{
                                    width: 140,
                                    height: 100,
                                    objectFit:
                                      "cover",
                                    borderRadius:
                                      10,
                                  }}
                                />
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {(hasErrors ||
                        hasWarnings) && (
                        <div className="bulk-error-box">
                          {(
                            product.validationErrors ||
                            []
                          ).map(
                            (
                              validationError,
                              index
                            ) => (
                              <p
                                key={`validation-${index}`}
                              >
                                <strong>
                                  Error:
                                </strong>{" "}
                                {typeof validationError ===
                                "object"
                                  ? validationError.message ||
                                    JSON.stringify(
                                      validationError
                                    )
                                  : validationError}
                              </p>
                            )
                          )}

                          {(
                            product.warnings || []
                          ).map(
                            (warning, index) => (
                              <p
                                key={`warning-${index}`}
                              >
                                <strong>
                                  Warning:
                                </strong>{" "}
                                {typeof warning ===
                                "object"
                                  ? warning.message ||
                                    JSON.stringify(
                                      warning
                                    )
                                  : warning}
                              </p>
                            )
                          )}
                        </div>
                      )}

                      {isExpanded && (
                        <div className="bulk-details">
                          <div className="bulk-detail-section">
                            <h4>
                              Product Details
                            </h4>

                            <div className="bulk-product-meta">
                              <span>
                                <strong>
                                  Short Description
                                </strong>

                                {product.short_description ||
                                  "-"}
                              </span>

                              <span>
                                <strong>
                                  Keywords
                                </strong>

                                {product.keywords ||
                                  "-"}
                              </span>

                              <span>
                                <strong>
                                  Tags
                                </strong>

                                {product.tags || "-"}
                              </span>

                              <span>
                                <strong>
                                  Discount
                                </strong>

                                {product.discount_type ||
                                  "none"}{" "}
                                {product.discount_value ||
                                  0}
                              </span>
                            </div>
                          </div>

                          <div className="bulk-detail-section">
                            <h4>
                              Media Summary
                            </h4>

                            <div className="bulk-product-meta">
                              <span>
                                <strong>
                                  Total Media
                                </strong>

                                {product.generalMediaCount ||
                                  0}
                              </span>

                              <span>
                                <strong>
                                  Images
                                </strong>

                                {product.generalImageCount ||
                                  0}
                              </span>

                              <span>
                                <strong>
                                  Videos
                                </strong>

                                {product.generalVideoCount ||
                                  0}
                              </span>

                              <span>
                                <strong>
                                  Folder Matched
                                </strong>

                                {product.matchedFolder
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </div>
                          </div>

                          <div className="bulk-detail-section">
                            <h4>
                              Option Templates
                            </h4>

                            {product.template_names
                              ?.length ? (
                              <div className="bulk-option-grid">
                                {product.template_names.map(
                                  (
                                    template,
                                    templateIndex
                                  ) => (
                                    <div
                                      key={`${template}-${templateIndex}`}
                                      className="bulk-option-pill"
                                    >
                                      <strong>
                                        {template}
                                      </strong>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : (
                              <p>
                                No templates
                                attached.
                              </p>
                            )}
                          </div>

                          <div className="bulk-detail-section">
                            <h4>Effective Option Values</h4>

                            {product.option_value_overrides?.length ? (
                              <div className="bulk-override-table-wrap">
                                <table className="bulk-override-table">
                                  <thead>
                                    <tr>
                                      <th>Template</th>
                                      <th>Field / Value</th>
                                      <th>Price adjustment</th>
                                      <th>Availability</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {product.option_value_overrides.map((override) => (
                                      <tr key={override.value_id}>
                                        <td>{override.template_key}</td>
                                        <td>
                                          {override.field_key} /{" "}
                                          {override.label || override.value_key}
                                        </td>
                                        <td>
                                          INR {Number(
                                            override.price_adjustment || 0
                                          ).toLocaleString("en-IN")}
                                        </td>
                                        <td>
                                          {override.is_available
                                            ? "Available"
                                            : "Unavailable"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p>No choice-value overrides are being applied.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>

            <div className="bulk-footer-actions">
              <button
                type="button"
                className="secondary-action"
                onClick={clearPreview}
              >
                Clear Preview
              </button>

              <button
                type="button"
                className="primary-action"
                onClick={confirmBulkUpload}
                disabled={
                  bulkLoading ||
                  getSelectedCount() === 0 ||
                  bulkPreview.fileErrors?.length > 0
                }
              >
                Confirm Bulk Upload
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductBulkUpload;
